import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "result", "password", "copy", "notice"]
  static values = { url: String, available: Boolean }

  connect() {
    this.active = true
    this.generation ||= 0
    this.passwordInput = this.passwordTarget
    this.resultPanel = this.resultTarget
    this.statusNotice = this.noticeTarget
    this.clear()
    this.buttonTarget.disabled = !this.availableValue
  }

  disconnect() {
    this.active = false
    this.request?.abort()
    this.request = null
    this.clear()
  }

  clear() {
    this.generation++
    this.passwordInput.value = ""
    this.resultPanel.hidden = true
    this.notice()
  }

  async reset(event) {
    event.preventDefault()
    if (this.buttonTarget.disabled) return

    this.clear()
    const generation = this.generation
    this.buttonTarget.disabled = true
    this.notice("Resetting password…")
    const request = new AbortController()
    this.request = request

    try {
      const response = await fetch(this.urlValue, {
        method: "POST", credentials: "same-origin", cache: "no-store", signal: request.signal,
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
      })
      if (response.redirected || !response.headers.get("content-type")?.includes("application/json")) {
        throw new Error("Refresh and sign in again before resetting the password.")
      }
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error || "The password could not be reset. Please try again.")
      if (typeof payload.password !== "string" || !payload.password) throw new Error("The new password was not returned. Please try again.")
      if (!this.active || generation !== this.generation) return

      this.passwordTarget.value = payload.password
      this.resultTarget.hidden = false
      this.notice("Password reset.")
    } catch (error) {
      if (this.active && generation === this.generation && error.name !== "AbortError") this.notice(error.message)
    } finally {
      if (this.active && this.request === request) this.buttonTarget.disabled = !this.availableValue
      if (this.request === request) this.request = null
    }
  }

  async copy() {
    const password = this.passwordTarget.value
    if (!password) return
    try {
      await navigator.clipboard.writeText(password)
      if (this.active && this.passwordTarget.value === password) this.notice("Password copied.")
    } catch {
      if (!this.active || this.passwordTarget.value !== password) return
      this.passwordTarget.focus()
      this.passwordTarget.select()
      this.notice("Select and copy the password manually.")
    }
  }

  notice(message = "") {
    this.statusNotice.textContent = message
    this.statusNotice.hidden = !message
  }
}
